import { onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'

export const fallbackDescription = 'Filmsomniac is a movie and TV diary for tracking watches, ratings, favourites and lists.'

function buildTabDescription(type, subject, section) {
  let description = ''
  switch (String(section).toLowerCase()) {
    case 'credits':
      if (type === 'film') description = `The essential cast and crew of ${subject}`
      if (type === 'show') description = `The essential cast of ${subject}`
      break
    case 'seasons':
      if (type === 'show') description = `Season listing of ${subject}`
      break
    case 'lists':
      description = `The user-generated lists where ${subject} appears`
      break
    case 'comments':
    case 'discussion':
      description = `Discuss ${subject}`
      break
    case 'you':
      description = `Your watch history of ${subject}`
      break
    case 'tv':
      if (type === 'person') description = `TV appearances of ${subject}`
      break
  }
  return description ? `${description} - Filmsomniac.com` : ''
}

export function buildMetaDescription(type, data = {}, section = '') {
  const title = String(data.title || '').trim()
  const name = String(data.name || '').trim()
  const username = String(data.username || '').trim()
  const showTitle = String(data.showTitle || '').trim()
  const year = String(data.year || '').trim()

  if (type === 'film' && title) {
    const subject = `${title}${year ? ` (${year})` : ''}`
    const tabDescription = buildTabDescription(type, subject, section)
    if (tabDescription) return tabDescription
    return `Track your watches of ${title}${year ? ` (${year})` : ''}, rate it, add it to lists and keep your movie diary on Filmsomniac.`
  }
  if (type === 'show' && title) {
    const subject = [title, String(data.years || '').trim()].filter(Boolean).join(' ')
    const tabDescription = buildTabDescription(type, subject, section)
    if (tabDescription) return tabDescription
    return `Track ${title}, record the episodes and seasons you've watched, rate the show and keep your TV diary on Filmsomniac.`
  }
  if (type === 'season' && title && data.number !== undefined && data.number !== null && data.number !== '') {
    const label = String(data.number) === '0' || data.number === 'specials' ? 'Specials' : `Season ${data.number}`
    const tabDescription = buildTabDescription(type, `${title} - ${label}`, section)
    if (tabDescription) return tabDescription
    return `Track ${title} ${label}, record the episodes you've watched and keep your TV viewing history on Filmsomniac.`
  }
  if (type === 'episode' && title && showTitle) {
    const code = String(data.code || '').trim()
    const subject = `${showTitle}${code ? ` ${code}` : ''} - ${title}`
    const tabDescription = buildTabDescription(type, subject, section)
    if (tabDescription) return tabDescription
    return `Record your watch of ${showTitle} – ${title} and keep track of your TV episodes on Filmsomniac.`
  }
  if (type === 'person' && name) {
    const tabDescription = buildTabDescription(type, name, section)
    if (tabDescription) return tabDescription
    return `Explore films and TV shows featuring ${name} on Filmsomniac.`
  }
  if (type === 'profile' && username) {
    return `See ${username}'s film and TV watches, ratings, favourites and lists on Filmsomniac.`
  }
  const listLabels = {
    'film-list': 'film',
    'show-list': 'TV show',
    'episode-list': 'episode',
    'person-list': 'person',
  }
  if (Object.hasOwn(listLabels, type) && title) {
    return `Explore ${title}, a curated ${listLabels[type]} list on Filmsomniac.`
  }
  return fallbackDescription
}

export function setMetaDescription(description = fallbackDescription) {
  let meta = document.querySelector('meta[name="description"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'description'
    document.head.appendChild(meta)
  }
  meta.content = description
}

export function useMetaDescription(description) {
  const route = useRoute()
  watch([() => route.fullPath, description], ([, value]) => {
    setMetaDescription(value)
  }, { immediate: true })
  onBeforeUnmount(() => setMetaDescription(fallbackDescription))
}
